/**
 * @see https://umijs.org/docs/max/access#access
 * */
import { salesPlatformNavigation } from './pages/frontend/permissions';
import { featureNavigation } from './utils/featurePermissions';

export default function access(
  initialState: { currentUser?: API.CurrentUser } | undefined,
) {
  const { currentUser } = initialState ?? {};
  const permissions = currentUser?.access;
  if (!permissions || typeof permissions !== 'object') return permissions;
  const granted = permissions as Record<string, unknown>;
  const feiShuApps = [
    'systemFeiShuApps',
    'systemFeiShuAppsCreate',
    'systemFeiShuAppsEdit',
    'systemFeiShuAppsSyncDepartments',
    'systemFeiShuAppsSyncMembers',
  ].some((code) => granted[code]);
  const workWxApps = [
    'systemWorkWxApps',
    'systemWorkWxAppsSyncMembers',
    'systemWorkWxAppsSyncContacts',
    'systemWorkWxAppsSyncTags',
    'systemWorkWxAppsSyncGroupChats',
  ].some((code) => granted[code]);
  const wechatAccess = Object.fromEntries(
    ['systemWechatPublicAccounts', 'systemWechatApps'].map((code) => [
      code,
      ['', 'Create', 'Edit', 'Delete', 'Toggle'].some(
        (action) => granted[`${code}${action}`],
      ),
    ]),
  );
  const applicationCenter = Boolean(
    wechatAccess.systemWechatPublicAccounts ||
      wechatAccess.systemWechatApps ||
      feiShuApps ||
      workWxApps,
  );
  const featureAccess = featureNavigation(granted);
  const systemNavigation = Boolean(
    granted['menu:system'] ||
      granted.system ||
      granted['material:list'] ||
      granted['short-link:list'] ||
      granted.systemOperationLog ||
      applicationCenter ||
      [
        'projectList',
        'systemCourse',
        'systemCoursePeriod',
        'systemProduct',
        'systemChannel',
        'systemDepartment',
        'systemDataPermission',
        'systemDataPermissionRoute',
        'systemDataProtection',
        'systemFeiShuTableConfigMapping',
      ].some((code) => featureAccess[code]),
  );
  return {
    ...granted,
    ...featureAccess,
    saleMarketIntentNavigation: [
      'saleMarketIntentQuestionnaire',
      'saleMarketIntentRespondent',
      'saleMarketIntentTask',
      'saleMarketIntentResult',
    ].some((code) => featureAccess[code]),
    ...wechatAccess,
    systemRole: [
      'systemRole',
      'systemRoleCreate',
      'systemRoleEdit',
      'systemRoleMembers',
    ].some((code) => granted[code]),
    dataCustomerTag: [
      'dataCustomerTag',
      'dataCustomerTagCreate',
      'dataCustomerTagCreateWorkWx',
      'dataCustomerTagEdit',
      'dataCustomerTagAssign',
      'dataCustomerTagSyncWorkWx',
    ].some((code) => granted[code]),
    dataCustomerBehavior: [
      'dataCustomerBehavior',
      'dataCustomerBehaviorUser',
      'dataCustomerBehaviorRecord',
      'dataCustomerBehaviorDefinition',
    ].some((code) => granted[code]),
    ...salesPlatformNavigation(granted),
    meetingSalesActivityWorkspace: [
      'Activity',
      'Registration',
      'Group',
      'Members',
      'Booking',
      'Rooming',
    ].some((page) =>
      [
        'List',
        'Edit',
        'Review',
        'Delete',
        'Export',
        'Auto',
        'Import',
        'Leaders',
        'Share',
        'CheckinCode',
        'StopRegistration',
        'ResumeRegistration',
      ].some((action) => granted[`meetingSales${page}${action}`]),
    ),
    meetingSalesHotelWorkspace: ['Hotel', 'Room', 'Booking', 'Rooming'].some(
      (page) =>
        [
          'List',
          'Edit',
          'Review',
          'Delete',
          'Export',
          'Auto',
          'Import',
          'Leaders',
          'Share',
          'CheckinCode',
        ].some((action) => granted[`meetingSales${page}${action}`]),
    ),
    systemFeiShuApps: feiShuApps,
    systemWorkWxApps: workWxApps,
    systemApplicationCenterNavigation: applicationCenter,
    'menu:systemApplicationCenterNavigation': applicationCenter,
    systemNavigation,
    'menu:systemNavigation': systemNavigation,
  };
}
